import React, { useRef } from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { useReactToPrint } from "react-to-print";

import { ColumnWidthInfo } from "../../../common/services/crudServices";
import { useRootStore } from "../../../../store/contexts";
import { BotType } from "../../../../api/graphql";
import { ShowString } from "../../../common/controls";
import { ShowNumber } from "../../../common/controls";
import { ShowMultiLine } from "../../../common/controls";
import { ShowDateTime } from "../../../common/controls";
import { InputCodeEditor } from "../../../common/controls";
import { ShowBoolean } from "../../../common/controls";
import { ShowReference } from "../../../common/controls";
import { ShowItemsCompact } from "../../../common/controls";
import { CrudRows } from "../../../common/types";
import { GridColumn } from "../../../common/types";
import { FormControl } from "@mui/material";

export type BotRunViewFormProps = {
  onPrint?: (handlePrint: () => void) => void;
  onCancel?: () => void;
  onEdit?: () => void;
  showToolbox?: boolean;
  botTypeOptions: { id: string; displayId: string }[];
  botType: BotType;
  runNumberValue?: { displayId: string; id: string } | null;
  runNumber: number;
  statusValue?: { displayId: string; id: string } | null;
  status: string;
  currentPhaseValue?: { displayId: string; id: string } | null;
  currentPhase: string;
  startedAtValue?: { displayId: string; id: string } | null;
  startedAt: Date | null;
  completedAtValue?: { displayId: string; id: string } | null;
  completedAt: Date | null;
  durationValue?: { displayId: string; id: string } | null;
  duration: number | undefined;
  paramsValue?: { displayId: string; id: string } | null;
  params: string;
  resultValue?: { displayId: string; id: string } | null;
  result: string;
  errorValue?: { displayId: string; id: string } | null;
  error: string;
  correlationIdValue?: { displayId: string; id: string } | null;
  correlationId: string;
  totalPhasesValue?: { displayId: string; id: string } | null;
  totalPhases: number;
  completedPhasesValue?: { displayId: string; id: string } | null;
  completedPhases: number;
  requiresHumanInterventionValue?: { displayId: string; id: string } | null;
  requiresHumanIntervention: boolean;
  interventionReasonValue?: { displayId: string; id: string } | null;
  interventionReason: string;
  logsPathValue?: { displayId: string; id: string } | null;
  logsPath: string;
  workerEventValue: { displayId: string; id: string } | null;
  triggeredByValue: { displayId: string; id: string } | null;
  relatedEntityTypeValue?: { displayId: string; id: string } | null;
  relatedEntityType: string;
  relatedEntityIdValue?: { displayId: string; id: string } | null;
  relatedEntityId: string;
  relatedEntityLabelValue?: { displayId: string; id: string } | null;
  relatedEntityLabel: string;
  cancellationRequestedAtValue?: { displayId: string; id: string } | null;
  cancellationRequestedAt: Date | null;
  cancellationRequestedByValue: { displayId: string; id: string } | null;
  cancellationReasonValue?: { displayId: string; id: string } | null;
  cancellationReason: string;
  phasesRows: CrudRows;
  phasesVisibleColumns: GridColumn[];
  phasesSetColumnWiths: (x: ColumnWidthInfo[]) => void;
  errorsRows: CrudRows;
  errorsVisibleColumns: GridColumn[];
  errorsSetColumnWiths: (x: ColumnWidthInfo[]) => void;
};

export const BotRunViewForm: React.FC<BotRunViewFormProps> = observer(
  ({
    onPrint = () => {},
    onCancel = () => {},
    onEdit,
    showToolbox = true,
    botTypeOptions = [],
    botType = "" as BotType,
    runNumber = 0,
    status = "",
    currentPhase = "",
    startedAt = null,
    completedAt = null,
    duration = 0,
    params = "{}",
    result = "{}",
    error = "{}",
    correlationId = "",
    totalPhases = 0,
    completedPhases = 0,
    requiresHumanIntervention = false,
    interventionReason = "",
    logsPath = "",
    workerEventValue,
    triggeredByValue,
    relatedEntityType = "",
    relatedEntityId = "",
    relatedEntityLabel = "",
    cancellationRequestedAt = null,
    cancellationRequestedByValue,
    cancellationReason = "",
    phasesRows,
    phasesVisibleColumns,
    phasesSetColumnWiths,
    errorsRows,
    errorsVisibleColumns,
    errorsSetColumnWiths,
  }) => {
    const rootStore = useRootStore();
    const { langStore } = rootStore;

    const componentRef = useRef(null);
    const handlePrint = useReactToPrint({
      content: () => componentRef.current,
    });

    return (
      <Grid>
        {showToolbox && (
          <FormToolbox
            editForm={onEdit}
            printForm={() => onPrint(handlePrint)}
            closeForm={() => onCancel()}
          />
        )}
        <Box ref={componentRef}>
          <GroupContainer
            id="null"
            title={langStore.groupBotRunLabel}
            collapsible={false}
          >
            <FormControl fullWidth>
              <ShowString
                value={
                  botType
                    ? botTypeOptions.find(
                        (o: { id: string }) => o.id === botType
                      )?.displayId || botType
                    : ""
                }
                label={langStore.BotRunBotTypeLabel}
              />
              <ShowNumber
                value={runNumber}
                label={langStore.BotRunRunNumberLabel}
              />
              <ShowMultiLine
                value={status}
                label={langStore.BotRunStatusLabel}
              />
              <ShowMultiLine
                value={currentPhase}
                label={langStore.BotRunCurrentPhaseLabel}
              />
              <ShowDateTime
                value={startedAt}
                label={langStore.BotRunStartedAtLabel}
              />
              <ShowDateTime
                value={completedAt}
                label={langStore.BotRunCompletedAtLabel}
              />
              <ShowNumber
                value={duration}
                label={langStore.BotRunDurationLabel}
              />
              <InputCodeEditor
                value={params}
                onChange={() => {}}
                label={langStore.BotRunParamsLabel}
                language="json"
                readOnly={true}
              />
              <InputCodeEditor
                value={result}
                onChange={() => {}}
                label={langStore.BotRunResultLabel}
                language="json"
                readOnly={true}
              />
              <InputCodeEditor
                value={error}
                onChange={() => {}}
                label={langStore.BotRunErrorLabel}
                language="json"
                readOnly={true}
              />
              <ShowMultiLine
                value={correlationId}
                label={langStore.BotRunCorrelationIdLabel}
              />
              <ShowNumber
                value={totalPhases}
                label={langStore.BotRunTotalPhasesLabel}
              />
              <ShowNumber
                value={completedPhases}
                label={langStore.BotRunCompletedPhasesLabel}
              />
              <ShowBoolean
                value={requiresHumanIntervention}
                label={langStore.BotRunRequiresHumanInterventionLabel}
              />
              <ShowMultiLine
                value={interventionReason}
                label={langStore.BotRunInterventionReasonLabel}
              />
              <ShowMultiLine
                value={logsPath}
                label={langStore.BotRunLogsPathLabel}
              />
              <ShowReference
                label={langStore.BotRunWorkerEventLabel}
                selectedValue={langStore.displayIdByLang(
                  workerEventValue?.displayId
                )}
              />
              <ShowReference
                label={langStore.BotRunTriggeredByLabel}
                selectedValue={langStore.displayIdByLang(
                  triggeredByValue?.displayId
                )}
              />
              <ShowMultiLine
                value={relatedEntityType}
                label={langStore.BotRunRelatedEntityTypeLabel}
              />
              <ShowMultiLine
                value={relatedEntityId}
                label={langStore.BotRunRelatedEntityIdLabel}
              />
              <ShowMultiLine
                value={relatedEntityLabel}
                label={langStore.BotRunRelatedEntityLabelLabel}
              />
              <ShowDateTime
                value={cancellationRequestedAt}
                label={langStore.BotRunCancellationRequestedAtLabel}
              />
              <ShowReference
                label={langStore.BotRunCancellationRequestedByLabel}
                selectedValue={langStore.displayIdByLang(
                  cancellationRequestedByValue?.displayId
                )}
              />
              <ShowMultiLine
                value={cancellationReason}
                label={langStore.BotRunCancellationReasonLabel}
              />
              <ShowItemsCompact
                rows={phasesRows}
                showLabel={true}
                label={langStore.BotRunPhasesLabel}
                columns={phasesVisibleColumns}
                setColumnWidths={phasesSetColumnWiths}
                entityName="BotPhase"
                enableDownload={true}
                onRowClick={(rowId) =>
                  rootStore.openSubentityModal("botPhase", rowId)
                }
              />
              <ShowItemsCompact
                rows={errorsRows}
                showLabel={true}
                label={langStore.BotRunErrorsLabel}
                columns={errorsVisibleColumns}
                setColumnWidths={errorsSetColumnWiths}
                entityName="BotError"
                enableDownload={true}
                onRowClick={(rowId) =>
                  rootStore.openSubentityModal("botError", rowId)
                }
              />
            </FormControl>
          </GroupContainer>
        </Box>
      </Grid>
    );
  }
);
